import * as yup from "yup";
import { useState } from "react";
import { toast } from "react-toastify";
import { Trans } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, SubmitHandler } from "react-hook-form";

import moment from "moment";
import Loaing from "../../icons/loaing";
import ConfirmModal from "../ConfirmModal";
import TextArea from "../event-selection/textArea";
import { putSafety } from "../../services/safetyService";
import SelectElement from "../event-selection/selectElement";
import { closeReporting } from "../../services/dashboardService";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { SafetyEventsPDF } from "./safety-events-pdf";
import SelectAndQmark from "../event-selection/select&Qmark";
import CustomCheckboxes from "../event-selection/customCheckboxes";
import DeletePopup from "./delete-popup";
import { UPDATE_VALUES } from "../../data/constants";
import FormDateInput from "../FormDateInput";
import AdminWrapper from "../misc/AdminWrapper";

export type updateSafetyTypes = {
  __v: number;
  _id: string;
  reporting: string;
  reportingId: string;
  eventDate: string;
  eventLocation: string;
  area: string;
  description: string;
  immediateConsequences: string;
  immediateConsequencesArray?: string[];
  consequencesHad: string;
  supervisionRateRespected: string;
  usualTeam: string;
  immediateCorrectiveAction: string;
  immediateCorrectiveActionArray?: string[];
  riskRelatedEvent: string;
  riskRelatedEventArray?: string[];
  eventAvoidedSaferEquipment: string;
  recurrence: string;
  origin: string;
  mistreatmentEvidence: string;
  professionalNames: string;
  insuranceBeenNotified: string;
  localAuthorityBeenNotified: string;
  printeventDate: boolean;
  printeventLocation: boolean;
  printarea: boolean;
  printdescription: boolean;
  printimmediateConsequences: boolean;
  printimmediateConsequencesArray: boolean;
  printconsequencesHad: boolean;
  printsupervisionRateRespected: boolean;
  printusualTeam: boolean;
  printimmediateCorrectiveAction: boolean;
  printimmediateCorrectiveActionArray: boolean;
  printriskRelatedEvent: boolean;
  printriskRelatedEventArray: boolean;
  printeventAvoidedSaferEquipment: boolean;
  printrecurrence: boolean;
  printprofessionalNames: boolean;
  printorigin: boolean;
  printmistreatmentEvidence: boolean;
  printinsuranceBeenNotified: boolean;
  printlocalAuthorityBeenNotified: boolean;
  setOpen: (value: boolean) => void;
  updateRecord: (value: any) => void;
  selectedPeriod: string;
};

const schema = yup.object({
  eventDate: yup.string().required(),
  eventLocation: yup.string().required(),
  area: yup.string().required(),
  description: yup.string().required(),
  immediateConsequencesArray: yup
    .array()
    .min(1, "immediateConsequencesArray is a required field!")
    .test(
      "no-empty-strings",
      "Array cannot contain empty string values",
      (value) => {
        return new Promise((resolve) => {
          resolve(value?.every((item) => item !== "") ?? false);
        });
      }
    ),
  consequencesHad: yup.string().required(),
  supervisionRateRespected: yup.string().required(),
  usualTeam: yup.string().required(),
  professionalNames: yup.string().required(),
  immediateCorrectiveActionArray: yup
    .array()
    .min(1, "immediateCorrectiveActions is a required field!")
    .test(
      "no-empty-strings",
      "Array cannot contain empty string values",
      (value) => {
        return new Promise((resolve) => {
          resolve(value?.every((item) => item !== "") ?? false);
        });
      }
    ),
  riskRelatedEventArray: yup
    .array()
    .min(1, "riskRelatedEvent is a required field!")
    .test(
      "no-empty-strings",
      "Array cannot contain empty string values",
      (value) => {
        return new Promise((resolve) => {
          resolve(value?.every((item) => item !== "") ?? false);
        });
      }
    ),
  eventAvoidedSaferEquipment: yup.string().required(),
  recurrence: yup.string().required(),
  origin: yup.string().required(),
  mistreatmentEvidence: yup.string().required(),
  insuranceBeenNotified: yup.string().required(),
  localAuthorityBeenNotified: yup.string().required(),
});

const UpdateSafetyEvents: React.FC<updateSafetyTypes> = ({
  reporting,
  reportingId,
  setOpen,
  updateRecord,
  selectedPeriod,
  ...props
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);
  const [pdfData, setPdfData] = useState<any>(null);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [date, setDate] = useState<string>(
    props.eventDate === "Invalid date"
      ? moment(selectedPeriod).format("YYYY-MM-DD")
      : props.eventDate
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<updateSafetyTypes>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...props,
    },
  });
  const handleDownload: SubmitHandler<updateSafetyTypes> = async (data) => {
    data.immediateConsequences =
      data.immediateConsequencesArray?.map((value) => value).join("-,-") ?? "";
    data.immediateCorrectiveAction =
      data.immediateCorrectiveActionArray?.map((value) => value).join("-,-") ??
      "";
    data.riskRelatedEvent =
      data.riskRelatedEventArray?.map((value) => value).join("-,-") ?? "";
    data.eventDate = moment(data.eventDate).format("YYYY-MM-DD");

    data.printriskRelatedEvent = data.printriskRelatedEventArray;
    data.printimmediateConsequences = data.printimmediateConsequencesArray;
    data.printimmediateCorrectiveAction =
      data.printimmediateCorrectiveActionArray;

    setPdfData(data);
  };

  const onSubmit: SubmitHandler<updateSafetyTypes> = async (data) => {
    setLoading(true);
    try {
      const {
        printarea,
        printconsequencesHad,
        printdescription,
        printeventAvoidedSaferEquipment,
        printeventDate,
        printeventLocation,
        printimmediateConsequences,
        printimmediateConsequencesArray,
        printimmediateCorrectiveAction,
        printimmediateCorrectiveActionArray,
        printinsuranceBeenNotified,
        printlocalAuthorityBeenNotified,
        printmistreatmentEvidence,
        printorigin,
        printrecurrence,
        printsupervisionRateRespected,
        printusualTeam,
        printriskRelatedEvent,
        printriskRelatedEventArray,
        printprofessionalNames,
        riskRelatedEventArray,
        immediateConsequencesArray,
        immediateCorrectiveActionArray,
        ...rest
      } = data;
      
      // Vérifier si le statut de maltraitance a changé
      const oldMistreatmentEvidence = props.mistreatmentEvidence;
      const newMistreatmentEvidence = data.mistreatmentEvidence;
      
      // Log pour debug
      console.log("Old mistreatmentEvidence:", oldMistreatmentEvidence);
      console.log("New mistreatmentEvidence:", newMistreatmentEvidence);
      
      rest.eventDate = moment(data.eventDate).format("YYYY-MM-DD");
      rest.riskRelatedEvent =
        data.riskRelatedEventArray?.map((value) => value).join("-,-") ?? "";
      rest.immediateConsequences =
        data.immediateConsequencesArray?.map((value) => value).join("-,-") ??
        "";
      rest.immediateCorrectiveAction =
        data.immediateCorrectiveActionArray
          ?.map((value) => value)
          .join("-,-") ?? "";

      const response = await putSafety(rest, data._id);

      if (response.message === "updated") {
        // Si la mise à jour de l'événement de sécurité est réussie
        
        // Si le statut de maltraitance passe de "no" à "yes"
        if (oldMistreatmentEvidence === "no" && newMistreatmentEvidence === "yes") {
          console.log("Changing status to 'Need Validation' because mistreatmentEvidence changed from no to yes");
          // Mettre à jour le statut du reporting
          await closeReporting(reportingId, { status: "Need Validation" });
          // Mettre à jour l'enregistrement avec le nouveau statut
          response.data.status = "Need Validation";
        }
        // Si le statut de maltraitance passe de "yes" à "no"
        else if (oldMistreatmentEvidence === "yes" && newMistreatmentEvidence === "no") {
          console.log("Changing status to 'Closed' because mistreatmentEvidence changed from yes to no");
          // Mettre à jour le statut du reporting
          await closeReporting(reportingId, { status: "Closed" });
          // Mettre à jour l'enregistrement avec le nouveau statut
          response.data.status = "Closed";
        }
        
        updateRecord(response?.data);
        toast.success("Your action has been successfully saved!");
        setLoading(false);
        setOpen(false);
      }
    } catch (error: any) {
      setLoading(false);
      toast.error(error);
    }
  };

  const resetForm = () => {
    setConfirmModalVisible(false);
    reset();
  };

  const updateReporting = async (value: string) => {
    setLoading(true);
    try {
      const response = await closeReporting(reportingId, { status: value });
      if (response.message === "updated") {
        toast.success("Your action has been successfully saved!");
        setLoading(false);
        updateRecord(response.data);
      }
    } catch (error: any) {
      setLoading(false);
      toast.error(error);
    }
  };
  const deleteEvent = (deleteConfirmed: boolean) => {
    if (deleteConfirmed) {
      setOpenDeletePopup(false);
      updateReporting(UPDATE_VALUES.DELETED);
    } else {
      setOpenDeletePopup(false);
    }
  };

  return (
    <section className="relative font-Poppins">
      <AdminWrapper>
        <DeletePopup
          deleteEvent={deleteEvent}
          open={openDeletePopup}
          setOpen={setOpenDeletePopup}
        />
      </AdminWrapper>
      <div>
        <div className="grid gap-3 lg:mx-10">
          <div className="w-16 h-16">
            <img
              src="/images/report.png"
              alt="!"
              className="max-w-full h-auto"
            />
          </div>
          <h1 className="text-4xl font-bold">
            <Trans i18nKey="Other Safety Events">Other Safety Events</Trans>
          </h1>
          <p className="text-xl text-[#6B6B6B]">
            <Trans i18nKey="safety_event_description">
              Please report here other serious events or near miss events
              involving safety of the children
            </Trans>
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-end lg:px-[20px] my-10 gap-[15px]">
          <AdminWrapper>
            {editEnabled && (
              <button
                type="button"
                className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
                onClick={() => setConfirmModalVisible(true)}
              >
                <Trans>Reset</Trans>
              </button>
            )}
            {!editEnabled && (
              <button
                type="button"
                className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
                onClick={() => setEditEnabled(true)}
              >
                <Trans>Edit</Trans>
              </button>
            )}
            <button
              type="button"
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              onClick={() => setOpenDeletePopup(true)}
            >
              <Trans>Delete</Trans>
            </button>
            <button
              type="button"
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              onClick={() => updateReporting(UPDATE_VALUES.CLOSED)}
            >
              <Trans>Validate</Trans>
            </button>
          </AdminWrapper>
          {pdfData ? (
            <PDFDownloadLink
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              document={<SafetyEventsPDF formData={pdfData ?? {}} />}
              fileName={"other-safety-events.pdf"}
            >
              {({ blob, url, loading, error }) =>
                loading ? "loading..." : "Download PDF"
              }
            </PDFDownloadLink>
          ) : (
            <button
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              onClick={handleSubmit(handleDownload)}
            >
              <Trans>Generate PDF</Trans>
            </button>
          )}
        </div>
        <div className="relative z-0 border border-gray-500 lg:mx-5">
          <div className="absolute -top-3 left-5 lg:-top-5 lg:left-14 bg-[white] px-[20px] lg:px-5">
            <h1 className="text-sm lg:text-3xl font-bold text-[#1E408E]">
              <Trans i18nKey="Event Details">Event Details</Trans>
            </h1>
          </div>
          <div className="md:p-16 sm:p-8 p-4 pt-8 md:pt-16">
            <div className="grid gap-5">
              <FormDateInput
                type="Date"
                register={register}
                errors={errors}
                name="eventDate"
                printEnabled
                label="Event date"
                placeholder=""
                translateId="Event date"
                disabled={!editEnabled}
                date={date}
                setDate={setDate}
                control={control}
              />
              <SelectElement
                register={register}
                printEnabled
                errors={errors}
                name="eventLocation"
                label="Event location"
                options={[
                  "Safety interior (School indoor premise)",
                  "Exterior space (School outdoor premise)",
                  "Safety exterior space (School outdoor premise)",
                ]}
                translateId="Event location"
                disabled={!editEnabled}
              />
              <SelectElement
                register={register}
                printEnabled
                errors={errors}
                name="origin"
                label="origin"
                options={[
                  "Team (negligence, non respect of procedure...)",
                  "Infra (broken furniture, faulty equipment...)",
                  "Other",
                ]}
                translateId="origin"
                disabled={!editEnabled}
              />
              <SelectAndQmark
                register={register}
                printEnabled
                errors={errors}
                name="mistreatmentEvidence"
                label="Is there any suspicion or evidence of abuse of one or more children? = child mistreatment"
                options={["yes", "no"]}
                translateId="mistreatmentEvidence"
                disabled={!editEnabled}
                i18nKey="Help Box - Other Safety Event - Is there any suspicion or evidence of child mistreatment ?"
              />

              <SelectElement
                register={register}
                printEnabled
                errors={errors}
                name="area"
                label="Area"
                options={[
                  "Playroom/activity room/classroom",
                  "Reception area, hallways, walkway",
                  "Restrooms",
                  "Garden",
                  "Technical area (kitchen, laundry room, storage room...)",
                  "Space reserved for professionals",
                  "Non applicable",
                ]}
                translateId="Area"
                disabled={!editEnabled}
              />
              <TextArea
                register={register}
                printEnabled
                errors={errors}
                name="description"
                label="Event Description (factual & taken care of elements)"
                translateId="description"
                disabled={!editEnabled}
              />

              <CustomCheckboxes
                printEnabled
                register={register}
                errors={errors}
                name="immediateConsequencesArray"
                options={[
                  {
                    text: "Personal injury to children and/or adults",
                  },
                  {
                    text: "Intrusion",
                  },
                  {
                    text: "Property damage",
                  },
                  {
                    text: "Temporary partial or total closure",
                  },
                  {
                    text: "No immediate consequences",
                  },
                  { text: "Other (specify in description)" },
                ]}
                label="The immediate consequences of the Safety incident"
                control={control}
                disabled={!editEnabled}
              />

              <CustomCheckboxes
                printEnabled
                register={register}
                errors={errors}
                name="immediateCorrectiveActionArray"
                options={[
                  {
                    text: "Evacuation",
                  },
                  {
                    text: "Safety (containment)",
                  },
                  {
                    text: "Rearrangement of the space",
                  },
                  {
                    text: "Awarness for the team, reminder of the protocol(s)",
                  },
                  {
                    text: "Review of the field organization",
                  },
                  { text: "Review of the schedules" },
                  {
                    text: "Removal of the object/furniture that caused the accident or the risk of accident",
                  },
                  {
                    text: "Locking down of the risk area / securing pending maintenance/work intervention",
                  },
                  {
                    text: "Emergency intervention for corrective repair/maintenance (e.g. window, anti-twist, sharp edges, etc.)",
                  },
                  {
                    text: "Others",
                  },
                ]}
                label="Immediate corrective actions"
                control={control}
                disabled={!editEnabled}
              />
              <SelectElement
                register={register}
                printEnabled
                errors={errors}
                name="insuranceBeenNotified"
                label="Has the insurance claim been filed?"
                options={["yes", "no"]}
                translateId="insuranceBeenNotifiedGuide"
                disabled={!editEnabled}
              />

              <SelectAndQmark
                register={register}
                printEnabled
                errors={errors}
                name="localAuthorityBeenNotified"
                label="Has the local authority been notified?"
                options={["yes", "no"]}
                translateId="localAuthorityBeenNotified"
                disabled={!editEnabled}
                i18nKey="Help Box - Other Safety Event - Has the local authority been informed?"
              />

              <SelectAndQmark
                register={register}
                printEnabled
                errors={errors}
                name="supervisionRateRespected"
                label="supervisionRateRespected"
                options={["yes", "no"]}
                translateId="supervisionRateRespected"
                disabled={!editEnabled}
                i18nKey="Help Box - Other Safety Event - When the event happened, was the staff ratio in compliance with regulations?"
              />
              <SelectElement
                register={register}
                printEnabled
                errors={errors}
                name="usualTeam"
                label="Usual team in the reception area?"
                options={["yes", "no"]}
                translateId="usualTeam"
                disabled={!editEnabled}
              />
              <TextArea
                register={register}
                errors={errors}
                printEnabled
                name="professionalNames"
                label="Name of all staff involved when the incident took place"
                translateId="professionalNames"
              />

              <CustomCheckboxes
                control={control}
                printEnabled
                register={register}
                errors={errors}
                name="riskRelatedEventArray"
                disabled={!editEnabled}
                options={[
                  {
                    text: "Risk of parents negatively influencing other parents in the school",
                  },
                  {
                    text: "Risk that parents will give bad publicity (social networks, negative comments on the internet)",
                  },
                  {
                    text: "Legal risk (lawsuit)",
                  },
                  {
                    text: "Risk of partial closure of the school",
                  },
                  {
                    text: "Risk of temporary closure of the school",
                  },
                  { text: "No" },
                ]}
                label="Outcomes related to the Safety incident"
              />
              <SelectElement
                register={register}
                printEnabled
                errors={errors}
                name="eventAvoidedSaferEquipment"
                label="Could the event have been avoided with safer equipment?"
                options={["yes", "no", "Not applicable"]}
                disabled={!editEnabled}
              />

              <SelectElement
                register={register}
                printEnabled
                errors={errors}
                name="consequencesHad"
                label="The consequences that the event could have had"
                options={[
                  "Personal injury to children and/or adults",
                  "Intrusion",
                  "Property damage",
                  "Other (specify in description)",
                ]}
                translateId="The consequences that the event could have had"
                disabled={!editEnabled}
              />
              <SelectElement
                register={register}
                printEnabled
                errors={errors}
                name="recurrence"
                label="Recurrence"
                options={[
                  "No, isolated incident",
                  "Yes, same type of event happened in the last 3 months",
                ]}
                translateId="Recurrence"
                disabled={!editEnabled}
              />
            </div>
          </div>
        </div>
        <AdminWrapper>
          <div className="flex justify-end lg:px-[20px] my-10">
            <button
              type={"submit"}
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
            >
              <Trans i18nKey="Submit">Submit</Trans>
            </button>
            {loading && <Loaing />}
          </div>
        </AdminWrapper>
      </form>
      <ConfirmModal
        isOpen={confirmModalVisible}
        setIsOpen={setConfirmModalVisible}
        onConfirm={resetForm}
      />
    </section>
  );
};

export default UpdateSafetyEvents;
